import React, { useState, useEffect, useCallback } from "react";
import {
  Activity, Filter, FileText, Search, Target, Link2,
  Share2, Radar, Layers, UserCheck, Globe, Settings,
  BarChart3, RefreshCw, ChevronDown,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "../api/client";

const CATEGORY_ICONS = {
  content: FileText,
  keywords: Search,
  audit: Settings,
  backlinks: Link2,
  syndication: Share2,
  local_rankings: Radar,
  pages: Layers,
  leads: UserCheck,
  wordpress: Globe,
  system: Settings,
};

const CATEGORY_COLORS = {
  content: "#B87333",
  keywords: "#3B82F6",
  audit: "#EAB308",
  backlinks: "#8B5CF6",
  syndication: "#06B6D4",
  local_rankings: "#10B981",
  pages: "#F97316",
  leads: "#EC4899",
  wordpress: "#6366F1",
  system: "#6B7280",
};

const CATEGORIES = [
  { value: "", label: "All Categories" },
  { value: "content", label: "Content" },
  { value: "keywords", label: "Keywords" },
  { value: "audit", label: "Audit" },
  { value: "backlinks", label: "Backlinks" },
  { value: "syndication", label: "Syndication" },
  { value: "local_rankings", label: "Local Rankings" },
  { value: "pages", label: "Landing Pages" },
  { value: "leads", label: "Leads" },
  { value: "wordpress", label: "WordPress" },
  { value: "system", label: "System" },
];

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function ActivityLog({ selectedSite }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);

  const siteId = selectedSite?.id;

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (category) params.category = category;
    if (siteId) params.website_id = siteId;

    Promise.all([
      api.get("/activity-log/", { params }),
      api.get("/activity-log/stats/", { params: siteId ? { website_id: siteId } : {} }),
    ])
      .then(([logsRes, statsRes]) => {
        setLogs(logsRes.data);
        setStats(statsRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, siteId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const chartData = (stats?.by_day || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    count: d.count,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="text-copper" size={24} />
          <h1 className="text-xl font-bold text-white">Activity Log</h1>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-3 py-2 bg-dark-200 text-gray-400 rounded-lg hover:text-white transition-colors"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-dark-300 border border-dark-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-white">{stats.total}</div>
            <div className="text-xs text-gray-500">Events (7 days)</div>
          </div>
          {Object.entries(stats.by_category || {})
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([cat, count]) => (
              <div key={cat} className="bg-dark-300 border border-dark-50 rounded-lg p-4">
                <div className="text-2xl font-bold" style={{ color: CATEGORY_COLORS[cat] || "#B87333" }}>
                  {count}
                </div>
                <div className="text-xs text-gray-500 capitalize">{cat.replace("_", " ")}</div>
              </div>
            ))}
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-dark-300 border border-dark-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Activity (Last 7 Days)</h3>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={chartData}>
              <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip
                contentStyle={{ background: "#16213E", border: "1px solid #1E3A5F", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#9CA3AF" }}
              />
              <Bar dataKey="count" fill="#B87333" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter size={14} className="text-gray-500" />
        <div className="relative">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-dark-200 border border-dark-50 text-gray-300 text-sm rounded-lg px-3 py-2 pr-8 appearance-none"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        </div>
      </div>

      {/* Activity Feed */}
      <div className="bg-dark-300 border border-dark-50 rounded-lg divide-y divide-dark-50">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No activity recorded yet.</div>
        ) : (
          logs.map((log) => {
            const Icon = CATEGORY_ICONS[log.category] || Activity;
            const color = CATEGORY_COLORS[log.category] || "#6B7280";
            return (
              <div key={log.id} className="flex items-start gap-3 px-4 py-3 hover:bg-dark-200/50 transition-colors">
                <div
                  className="mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: `${color}20` }}
                >
                  <Icon size={14} style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white">{log.action}</div>
                  {log.detail && (
                    <div className="text-xs text-gray-500 mt-0.5 truncate">{log.detail}</div>
                  )}
                </div>
                <div className="flex flex-col items-end flex-shrink-0">
                  <span className="text-[10px] text-gray-600">{timeAgo(log.created_at)}</span>
                  <span className="text-[10px] text-gray-700">{log.username}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
